/**
 * ExcelPromptPopover — free-text prompt box anchored under the Excel
 * widget's prompt button.
 *
 * The textarea grows with its content (up to a max height, then scrolls).
 * Escape, the close icon, or a click outside dismisses it; Enter (or the
 * Submit button) hands the trimmed text to the caller, which keeps the box
 * open until the edit lands so a failed run can be retried.
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Loader2, X } from 'lucide-react';

import styles from './ExcelPromptPopover.module.css';

const GAP = 6;
const MARGIN = 8;

export interface ExcelPromptPopoverProps {
  anchorEl: HTMLElement;
  busy: boolean;
  error: string | null;
  onSubmit: (text: string) => void;
  onClose: () => void;
}

export const ExcelPromptPopover: React.FC<ExcelPromptPopoverProps> = ({
  anchorEl,
  busy,
  error,
  onSubmit,
  onClose,
}) => {
  const [text, setText] = useState<string>('');
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const popoverRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useLayoutEffect(() => {
    const node = textareaRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }, [text]);

  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    const anchor = anchorEl.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    let x = anchor.left;
    let y = anchor.bottom + GAP;
    if (x + rect.width > window.innerWidth - MARGIN) {
      x = Math.max(MARGIN, window.innerWidth - rect.width - MARGIN);
    }
    if (y + rect.height > window.innerHeight - MARGIN) {
      y = Math.max(MARGIN, anchor.top - rect.height - GAP);
    }
    setPos((prev) => (prev && prev.x === x && prev.y === y ? prev : { x, y }));
  }, [anchorEl, text, error]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    const onMouseDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorEl.contains(target)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onMouseDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, [anchorEl, onClose]);

  const canSubmit = text.trim().length > 0 && !busy;

  const handleSubmit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    onSubmit(trimmed);
  }, [text, busy, onSubmit]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      ref={popoverRef}
      className={styles.popover}
      style={{ left: pos?.x ?? 0, top: pos?.y ?? 0, visibility: pos ? 'visible' : 'hidden' }}
      role="dialog"
      aria-label="Excel prompt"
      data-testid="excel-prompt-popover"
    >
      <button
        type="button"
        className={styles.closeButton}
        onClick={onClose}
        aria-label="Close prompt"
        title="Close"
        data-testid="excel-prompt-close"
      >
        <X size={13} aria-hidden="true" />
      </button>

      <textarea
        ref={textareaRef}
        className={styles.input}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={handleKeyDown}
        disabled={busy}
        rows={1}
        placeholder="Describe the change to make to this sheet…"
        aria-label="Prompt"
        data-testid="excel-prompt-input"
      />

      {error ? (
        <p className={styles.error} role="alert" data-testid="excel-prompt-error">
          {error}
        </p>
      ) : null}

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={!canSubmit}
          title="Submit (Enter)"
          data-testid="excel-prompt-submit"
        >
          {busy ? (
            <Loader2 size={13} aria-hidden="true" className={styles.spinner} />
          ) : null}
          <span>{busy ? 'Working…' : 'Submit'}</span>
        </button>
      </div>
    </div>
  );
};

export default ExcelPromptPopover;
